import { Check } from 'lucide-react';
import { Icon } from '@iconify/react';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { Box, TextField, IconButton, Autocomplete, CircularProgress } from '@mui/material';

import { useTranslate } from 'src/locales';

const STORAGE_KEYS = {
  SELECTED_BRANCH: 'selected_branch',
  USER: 'user',
  ACCESS_LEVEL: 'access_level_key',
  BRANCH_SHORT: 'branches_short_key',
};

export function BranchWorkspace(props) {
  const { t } = useTranslate();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const accessLevel = useMemo(() => {
    const accessLevelData = localStorage.getItem(STORAGE_KEYS.ACCESS_LEVEL);
    return accessLevelData ? JSON.parse(accessLevelData) : null;
  }, []);

  const getBranches = useCallback(() => {
    const branchesData = localStorage.getItem(STORAGE_KEYS.BRANCH_SHORT);
    return branchesData ? JSON.parse(branchesData) : [];
  }, []);

  const getBranchFromUserSession = useCallback(() => {
    const userSessionData = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userSessionData) return null;
    try {
      const { branchcode } = JSON.parse(userSessionData);
      const branches = getBranches();
      return branches.find((b) => b.branchCode === branchcode || b.branchShort === branchcode);
    } catch (error) {
      console.error('Error parsing user session data:', error);
      return null;
    }
  }, [getBranches]);

  const getBranchFromStorage = useCallback(() => {
    const storedBranchData = localStorage.getItem(STORAGE_KEYS.SELECTED_BRANCH);
    if (!storedBranchData) return null;
    try {
      const { branchShort } = JSON.parse(storedBranchData);
      const branches = getBranches();
      return branches.find((b) => b.branchShort === branchShort);
    } catch (error) {
      console.error('Error parsing stored branch data:', error);
      return null;
    }
  }, [getBranches]);

  const updateBranchStorage = useCallback((branch) => {
    if (branch) {
      const branchData = {
        branchShort: branch.branchShort,
        branchName: branch.branchName,
        branchCode: branch.branchCode,
        sub: branch.sub,
      };
      localStorage.setItem(STORAGE_KEYS.SELECTED_BRANCH, JSON.stringify(branchData));
      window.dispatchEvent(new CustomEvent('branchChanged', { detail: branchData }));
    }
  }, []);

  const handleBranchChange = useCallback(
    (event, newValue) => {
      if (!newValue) return;
      setLoading(true);
      setSuccess(false);
      setSelectedBranch(newValue);
      updateBranchStorage(newValue);
      setTimeout(() => {
        setLoading(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 1000);
      }, 3500);
      window.location.reload();
    },
    [updateBranchStorage]
  );

  // const initializeBranch = useCallback(() => {
  //   const branch = getBranchFromUserSession() || getBranchFromStorage() || getBranches()[0];
  //   if (branch) {
  //     setSelectedBranch(branch);
  //     setInputValue(`${branch.branchShort}${branch.branchName ? ` - ${branch.branchName}` : ''}`);
  //     updateBranchStorage(branch);
  //   }
  // }, [getBranchFromUserSession, getBranchFromStorage, getBranches, updateBranchStorage]);

  const initializeBranch = useCallback(() => {
    const branchInStorage = getBranchFromStorage();
    if (!selectedBranch && !branchInStorage) {
      const branch = getBranchFromUserSession() || getBranches()[0];
      if (branch) {
        if (!selectedBranch || selectedBranch.branchShort !== branch.branchShort) {
          setSelectedBranch(branch);
          setInputValue(
            `${branch.branchShort}${branch.branchName ? ` - ${branch.branchName}` : ''}`
          );
          updateBranchStorage(branch);
        }
      }
    } else if (
      branchInStorage &&
      (!selectedBranch || branchInStorage.branchShort !== selectedBranch.branchShort)
    ) {
      setSelectedBranch(branchInStorage);
      setInputValue(
        `${branchInStorage.branchShort}${branchInStorage.branchName ? ` - ${branchInStorage.branchName}` : ''}`
      );
    }
  }, [
    getBranchFromUserSession,
    getBranchFromStorage,
    getBranches,
    updateBranchStorage,
    selectedBranch,
  ]);

  useEffect(() => {
    initializeBranch();
  }, [initializeBranch]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (
        [STORAGE_KEYS.SELECTED_BRANCH, STORAGE_KEYS.USER, STORAGE_KEYS.BRANCH_SHORT].includes(
          event.key
        )
      ) {
        const branchInStorage = getBranchFromStorage();
        if (
          branchInStorage &&
          (!selectedBranch || branchInStorage.branchShort !== selectedBranch.branchShort)
        ) {
          setSelectedBranch(branchInStorage);
          setInputValue(
            `${branchInStorage.branchShort}${branchInStorage.branchName ? ` - ${branchInStorage.branchName}` : ''}`
          );
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [getBranchFromStorage, selectedBranch]);

  // useEffect(() => {
  //   initializeBranch();
  // }, [initializeBranch]);

  // useEffect(() => {
  //   const handleStorageChange = (event) => {
  //     if (
  //       [STORAGE_KEYS.SELECTED_BRANCH, STORAGE_KEYS.USER, STORAGE_KEYS.BRANCH_SHORT].includes(
  //         event.key
  //       )
  //     ) {
  //       initializeBranch();
  //     }
  //   };
  //   window.addEventListener('storage', handleStorageChange);
  //   return () => window.removeEventListener('storage', handleStorageChange);
  // }, [initializeBranch]);

  if (accessLevel !== 1) {
    const branchShort = localStorage.getItem(STORAGE_KEYS.BRANCH_SHORT);
    if (branchShort) {
      const branches = getBranches();
      const branch = branches.find((b) => b.branchShort === branchShort);
      if (branch) {
        updateBranchStorage(branch);
      }
    }
    return null;
  }

  const handleClearClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setInputValue('');
    setSelectedBranch(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }} {...props}>
      <Autocomplete
        size="small"
        sx={{
          width: {
            xs: '100%',
            sm: 300,
          },
        }}
        options={getBranches()}
        getOptionLabel={(option) =>
          `${option.branchShort}${option.branchName ? ` - ${option.branchName}` : ''}`
        }
        value={selectedBranch}
        onChange={handleBranchChange}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={t('Select Branch')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {success && <Check size={20} color="green" />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.branchShort === value.branchShort}
        clearOnBlur={false}
        clearOnEscape={false}
        clearIcon={
          <IconButton onClick={handleClearClick}>
            <Icon icon="ic:baseline-clear" />
          </IconButton>
        }
      />
    </Box>
  );
}
